import {
  BleClient,
  type BleService,
  type ScanResult,
} from "@capacitor-community/bluetooth-le";
import { Capacitor } from "@capacitor/core";
import { t } from "@lingui/core/macro";
import { DevicePrinterUserError } from "@/utils/pos-printer/printer-errors";

/**
 * Serial service/characteristic advertised by most generic BLE ESC/POS
 * thermal printers (verified against the printers used by our customers).
 * Printers that don't expose this pair fall back to the first writable
 * characteristic of a vendor service.
 */
export const BT_PRINTER_SERVICE = "e7810a71-73ae-499d-8c15-faa9aef0c3f2";
const BT_PRINTER_CHARACTERISTIC = "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f";

/**
 * Standard GATT services that never carry the print characteristic —
 * skipped when falling back to "first writable characteristic".
 */
const NON_PRINTING_STANDARD_SERVICES = new Set([
  "00001800-0000-1000-8000-00805f9b34fb", // Generic Access
  "00001801-0000-1000-8000-00805f9b34fb", // Generic Attribute
  "0000180a-0000-1000-8000-00805f9b34fb", // Device Information
  "0000180f-0000-1000-8000-00805f9b34fb", // Battery
]);

const initializedBle = (() => {
  let initializePromise: Promise<void> | null = null;
  return () => {
    // Only memoize success — a rejected initialize (e.g. the user denied the
    // Bluetooth permission dialog) must be retried on the next user action so
    // the OS permission prompt can be shown again.
    initializePromise ??= BleClient.initialize({
      androidNeverForLocation: true,
    }).catch((error) => {
      initializePromise = null;
      throw error;
    });
    return initializePromise;
  };
})();

export async function isBluetoothEnabled(): Promise<boolean> {
  await initializedBle();
  return await BleClient.isEnabled();
}

export async function startBtPrinterScan(
  onResult: (result: ScanResult) => void,
): Promise<void> {
  await initializedBle();

  if (!(await BleClient.isEnabled())) {
    throw new DevicePrinterUserError(
      t`Bluetooth je izklopljen. Vklopite ga v nastavitvah naprave.`,
    );
  }

  await BleClient.requestLEScan({ allowDuplicates: false }, onResult);
}

export async function stopBtPrinterScan(): Promise<void> {
  try {
    await BleClient.stopLEScan();
  } catch {
    // Stopping a scan that never started is a no-op.
  }
}

/**
 * On Android 11 and older the OS silently suppresses all BLE scan results
 * while system Location Services are off, even with the location permission
 * granted. Used to explain an empty scan instead of failing silently.
 */
export async function isAndroidLocationOff(): Promise<boolean> {
  if (Capacitor.getPlatform() !== "android") {
    return false;
  }

  try {
    return !(await BleClient.isLocationEnabled());
  } catch {
    return false;
  }
}

/**
 * Device ids we connected to since app start. Connections are kept open
 * between receipts (POS prints several in a row); the disconnect callback
 * removes entries when the printer drops the link or powers off.
 */
const connectedDeviceIds = new Set<string>();

async function connectToPrinter(deviceId: string): Promise<void> {
  if (connectedDeviceIds.has(deviceId)) {
    return;
  }

  // On iOS a peripheral must be known to the central before `connect` — after
  // an app restart a persisted deviceId is only re-resolvable via getDevices.
  // On Android this is a cheap lookup by MAC address.
  const knownDevices = await BleClient.getDevices([deviceId]).catch(() => null);
  if (knownDevices !== null && knownDevices.length === 0) {
    // iOS could not resolve the persisted peripheral UUID (new phone after a
    // migration/restore, or the system evicted the unbonded peripheral) —
    // "check the printer is in range" would be misleading here.
    throw new DevicePrinterUserError(
      t`Shranjenega tiskalnika ni mogoče najti. Ponovno ga izberite v nastavitvah tiskalnika računov.`,
    );
  }

  try {
    await BleClient.connect(
      deviceId,
      (disconnectedDeviceId) => {
        connectedDeviceIds.delete(disconnectedDeviceId);
      },
      { timeout: 10_000 },
    );
  } catch (error) {
    throw new DevicePrinterUserError(
      t`Povezava z Bluetooth tiskalnikom ni uspela. Preverite, da je tiskalnik vklopljen in v dosegu.`,
      { cause: error },
    );
  }

  connectedDeviceIds.add(deviceId);
}

type WriteTarget = {
  service: string;
  characteristic: string;
  withResponse: boolean;
};

function pickWriteTarget(services: BleService[]): WriteTarget | null {
  const toTarget = (
    service: BleService,
    preferredCharacteristic?: string,
  ): WriteTarget | null => {
    const writableCharacteristics = service.characteristics.filter(
      (c) => c.properties.write || c.properties.writeWithoutResponse,
    );
    const characteristic =
      writableCharacteristics.find(
        (c) => c.uuid.toLowerCase() === preferredCharacteristic,
      ) ?? writableCharacteristics[0];

    if (!characteristic) return null;

    return {
      service: service.uuid,
      characteristic: characteristic.uuid,
      withResponse: characteristic.properties.write,
    };
  };

  const knownService = services.find(
    (s) => s.uuid.toLowerCase() === BT_PRINTER_SERVICE,
  );
  if (knownService) {
    const target = toTarget(knownService, BT_PRINTER_CHARACTERISTIC);
    if (target) return target;
  }

  for (const service of services) {
    if (NON_PRINTING_STANDARD_SERVICES.has(service.uuid.toLowerCase())) {
      continue;
    }
    const target = toTarget(service);
    if (target) return target;
  }

  return null;
}

async function getWriteChunkSize(deviceId: string): Promise<number> {
  try {
    // Android negotiates MTU on connect; 3 bytes are the ATT header.
    const mtu = await BleClient.getMtu(deviceId);
    return Math.min(Math.max(mtu - 3, 20), 512);
  } catch {
    // iOS does not expose the MTU — 150 bytes stays under the usual
    // 185-byte negotiated limit of BLE printers.
    return 150;
  }
}

/** A chunked write that failed after `bytesWritten` bytes reached the printer. */
class BtWriteError extends Error {
  constructor(
    readonly bytesWritten: number,
    options?: ErrorOptions,
  ) {
    super(`Bluetooth write failed after ${bytesWritten} bytes`, options);
    this.name = "BtWriteError";
  }
}

async function writeBytes(deviceId: string, bytes: Uint8Array): Promise<void> {
  const services = await BleClient.getServices(deviceId);
  const target = pickWriteTarget(services);

  if (!target) {
    throw new DevicePrinterUserError(
      t`Izbrana Bluetooth naprava ni podprt tiskalnik.`,
    );
  }

  const chunkSize = await getWriteChunkSize(deviceId);

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.slice(offset, offset + chunkSize);
    const value = new DataView(chunk.buffer);

    try {
      if (target.withResponse) {
        await BleClient.write(
          deviceId,
          target.service,
          target.characteristic,
          value,
        );
      } else {
        await BleClient.writeWithoutResponse(
          deviceId,
          target.service,
          target.characteristic,
          value,
        );
        // No ACK-based flow control without response — give the printer's
        // buffer time to drain between chunks.
        await new Promise((resolve) => setTimeout(resolve, 20));
      }
    } catch (error) {
      throw new BtWriteError(offset, { cause: error });
    }
  }
}

/**
 * Sends rendered ESC/POS bytes to the given BLE printer: connects (reusing an
 * open connection when possible), finds the writable characteristic, and
 * writes the data in MTU-sized chunks. Retries once with a fresh connection
 * when the first write fails — printers commonly keep a stale link after
 * being power-cycled.
 */
export async function printEscPosOverBluetooth(
  deviceId: string,
  bytes: Uint8Array,
): Promise<void> {
  await initializedBle();

  if (!(await BleClient.isEnabled())) {
    throw new DevicePrinterUserError(
      t`Bluetooth je izklopljen. Vklopite ga v nastavitvah naprave.`,
    );
  }

  await connectToPrinter(deviceId);

  try {
    await writeBytes(deviceId, bytes);
  } catch (error) {
    if (error instanceof DevicePrinterUserError) {
      throw error;
    }

    // Retry only when nothing reached the printer yet (typically a stale
    // connection after the printer was power-cycled). Resending after a
    // mid-stream failure would print a duplicated, garbled receipt.
    if (error instanceof BtWriteError && error.bytesWritten > 0) {
      throw error;
    }

    connectedDeviceIds.delete(deviceId);
    try {
      await BleClient.disconnect(deviceId);
    } catch {
      // Already disconnected.
    }

    await connectToPrinter(deviceId);
    await writeBytes(deviceId, bytes);
  }
}
